import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Splitter, SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { ApplicationDocumentList, ApplicationDocument, MemoizedViewer } from '@amsanalytics/react-shared-library';
import { viewerHttpGet, viewerHttpGetFile, viewerHttpPostFormData } from '../../../Http/ViewerHttpClient';
import { saveFile } from '../../../Utils';
import styles from './_document-viewer-page.module.scss';

/**
 * DocumentViewerPage — embedded, chrome-less document viewer for AMS 2000.
 *
 * SPECIAL ROUTE: This page does NOT participate in the app's normal `:clientId`
 * URL convention. It is loaded via iframe with a viewer token in the URL fragment
 * (#token=...) and authenticates via Authorization: ViewerToken {token}.
 * It must remain excluded from any MSAL-gated bootstrapping.
 */

type ViewerState = 'loading' | 'ready' | 'empty' | 'error' | 'expired';

const supportedDocTypes: string[] = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf',
];

const UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

// Viewer endpoints return three body shapes depending on the failure:
// problem+json from Problem(...), { error } from ViewerTokenAuthFilter, and plain text
// from BadRequest/NotFound string overloads. Server-side 413 from RequestSizeLimit may
// have no parseable body at all. Try each shape in order; fall back to a status-based message.
async function extractErrorMessage(response: Response, fallback: string): Promise<string> {
  let rawBody = '';
  try {
    rawBody = await response.text();
  } catch {
    return fallback;
  }

  if (!rawBody) return fallback;

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('json')) {
    try {
      const parsed = JSON.parse(rawBody);
      const message =
        parsed?.detail ||
        parsed?.title ||
        parsed?.error ||
        parsed?.message;
      if (typeof message === 'string' && message.trim()) return message.trim();
    } catch {
      // Fall through to plain-text handling.
    }
  }

  const trimmed = rawBody.trim();
  return trimmed.length > 0 && trimmed.length < 500 ? trimmed : fallback;
}

export default function DocumentViewerPage() {
  const [viewerState, setViewerState] = useState<ViewerState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [documents, setDocuments] = useState<ApplicationDocument[]>([]);
  const [documentListRenderKey, setDocumentListRenderKey] = useState(0);
  const [expiresUtc, setExpiresUtc] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<ApplicationDocument | undefined>();
  const [transientError, setTransientError] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);

  const [panes, setPanes] = useState<SplitterPaneProps[]>([
    { resizable: true },
    { size: '350px', resizable: true },
  ]);

  const handleExpired = useCallback(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    setViewerState('expired');
  }, []);

  const handleUnauthorized = useCallback(async (response: Response): Promise<void> => {
    try {
      const body = await response.json();
      if (body?.error === 'token_expired') {
        handleExpired();
        return;
      }
    } catch {
      // Response body wasn't JSON — treat as invalid token
    }

    setViewerState('error');
    setErrorMessage('Invalid viewer token. Please request a new link.');
  }, [handleExpired]);

  const loadDocuments = useCallback(async (token: string) => {
    try {
      const response = await viewerHttpGet('viewer/documents', token);
      if (response.status === 401) {
        await handleUnauthorized(response);
        return;
      }
      if (!response.ok) {
        setViewerState('error');
        setErrorMessage('Failed to load documents.');
        return;
      }

      const data = await response.json();
      setExpiresUtc(data.expiresUtc);
      setDocuments(data.documents || []);
      // Force ApplicationDocumentList to remount after reload. The shared library's
      // list items cache props.document in local state on mount and never resync,
      // so in-place updates (e.g. a newly uploaded doc shifting existing rows down)
      // render stale names until remount. Remove once the library is fixed.
      setDocumentListRenderKey((current) => current + 1);
      setViewerState(data.documents?.length > 0 ? 'ready' : 'empty');
    } catch (e) {
      setViewerState('error');
      setErrorMessage('Network error. Please try again.');
    }
  }, [handleUnauthorized]);

  // --- Token extraction and scrubbing ---
  useEffect(() => {
    // If we already have a token (e.g. React strict mode re-run after scrub), skip.
    if (tokenRef.current) return;

    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const tokenParam = hashParams.get('token');
    if (!tokenParam) {
      setViewerState('error');
      setErrorMessage('No viewer token provided.');
      return;
    }

    let token = tokenParam;
    try {
      token = decodeURIComponent(tokenParam);
    } catch {
      // Leave the raw value if it is not URL-encoded.
    }

    tokenRef.current = token;

    // Scrub token from URL and browser history immediately
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    // Load documents
    loadDocuments(token);
  }, [loadDocuments]);

  // --- Expiry timer ---
  useEffect(() => {
    if (!expiresUtc) return;

    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    const msUntilExpiry = new Date(expiresUtc).getTime() - Date.now();
    if (msUntilExpiry <= 0) {
      handleExpired();
      return;
    }

    expiryTimerRef.current = setTimeout(() => {
      handleExpired();
    }, msUntilExpiry);

    return () => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, [expiresUtc, handleExpired]);

  const handleSelectDocument = (doc: ApplicationDocument) => {
    setSelectedDocument(doc);
  };

  const handleDownloadDocument = async (doc: ApplicationDocument) => {
    if (!tokenRef.current || !doc.documentId) return;

    setTransientError(null);

    try {
      const response = await viewerHttpGetFile(
        `viewer/documents/${doc.documentId}/download`,
        tokenRef.current
      );

      if (response.status === 401) {
        await handleUnauthorized(response);
        return;
      }

      if (response.ok) {
        await saveFile(response, doc.name || 'document');
        return;
      }

      const fallback =
        response.status === 404
          ? 'Document not found.'
          : 'Download failed. Please try again.';
      setTransientError(await extractErrorMessage(response, fallback));
    } catch (e) {
      console.error('Download failed:', e);
      setTransientError('Network error. Please try again.');
    }
  };

  const handleUploadDocuments = async (files: File[]) => {
    if (!tokenRef.current || files.length === 0) return;

    setTransientError(null);

    // Short-circuit oversized uploads before the request — the server-side RequestSizeLimit
    // often closes the connection mid-upload without a parseable response body, which would
    // otherwise surface as a generic "Network error" in the catch block.
    const oversized = files.find((f) => f.size > UPLOAD_MAX_BYTES);
    if (oversized) {
      setTransientError(`"${oversized.name}" exceeds the 50 MB upload limit.`);
      return;
    }
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > UPLOAD_MAX_BYTES) {
      setTransientError('Total upload size exceeds the 50 MB limit. Upload fewer files at a time.');
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));

    try {
      const response = await viewerHttpPostFormData(
        'viewer/documents',
        tokenRef.current,
        formData
      );

      if (response.status === 401) {
        await handleUnauthorized(response);
        return;
      }

      if (response.ok) {
        // Reload document list after successful upload
        await loadDocuments(tokenRef.current!);
        return;
      }

      const fallback =
        response.status === 413
          ? 'File too large. Maximum upload size is 50 MB.'
          : response.status === 400
          ? 'Upload rejected. Check the file and try again.'
          : 'Upload failed. Please try again.';
      setTransientError(await extractErrorMessage(response, fallback));
    } catch (e) {
      console.error('Upload failed:', e);
      setTransientError('Network error. Please try again.');
    }
  };

  // --- Render status overlays ---
  if (viewerState === 'loading') {
    return (
      <div className={styles.documentViewerPage}>
        <div className={styles.statusOverlay}>
          <p>Loading documents...</p>
        </div>
      </div>
    );
  }

  if (viewerState === 'expired') {
    return (
      <div className={styles.documentViewerPage}>
        <div className={styles.statusOverlay}>
          <h2>Session Expired</h2>
          <p>Session expired. Please re-open the document viewer.</p>
        </div>
      </div>
    );
  }

  if (viewerState === 'error') {
    return (
      <div className={styles.documentViewerPage}>
        <div className={styles.statusOverlay}>
          <h2>Error</h2>
          <p>{errorMessage}</p>
        </div>
      </div>
    );
  }

  // --- Main viewer (ready or empty) ---
  return (
    <div className={styles.documentViewerPage} ref={viewerContainerRef}>
      {transientError && (
        <div className={styles.errorBanner} role="alert">
          <span className={styles.errorBannerMessage}>{transientError}</span>
          <button
            type="button"
            className={styles.errorBannerDismiss}
            aria-label="Dismiss"
            onClick={() => setTransientError(null)}
          >
            ×
          </button>
        </div>
      )}
      <div className={styles.viewerContainer}>
        <Splitter
          onChange={(event: SplitterOnChangeEvent) => setPanes(event.newState)}
          panes={panes}
          style={{ height: '100%' }}
        >
          {/* Left pane: Document preview */}
          <div style={{ height: '100%', position: 'relative' }}>
            {selectedDocument?.preSignedUrl ? (
              selectedDocument?.mimeType === 'application/pdf' ? (
                <iframe
                  src={selectedDocument?.preSignedUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    backgroundColor: '#f5f5f5',
                  }}
                  title={selectedDocument?.name || 'Document Viewer'}
                />
              ) : (
                <MemoizedViewer
                  style={{ height: '100%' }}
                  selectedDocumentName={selectedDocument?.name}
                  selectedDocumentMimeType={selectedDocument?.mimeType}
                  selectedDocumentUrl={selectedDocument?.preSignedUrl}
                  container={viewerContainerRef?.current || undefined}
                  supportedDocTypes={supportedDocTypes}
                />
              )
            ) : (
              <div className={styles.previewPlaceholder}>
                {viewerState === 'empty'
                  ? 'No documents available. Use the upload area to add documents.'
                  : 'Select a document to view'}
              </div>
            )}
          </div>

          {/* Right pane: Document list */}
          <div style={{ height: '100%' }}>
            <ApplicationDocumentList
              key={documentListRenderKey}
              applicationDocuments={documents}
              applicationCustomerId={''}
              supportedDocTypes={supportedDocTypes}
              stipulations={[]}
              onUploadDocument={handleUploadDocuments}
              onSelectDocument={handleSelectDocument}
              onDownloadDocument={handleDownloadDocument}
              canDelete={false}
              hideStipulations={true}
              onToggleCanViewInCustomerPortal={() => {}}
            />
          </div>
        </Splitter>
      </div>
    </div>
  );
}
